// Catálogo de tools que se le pasa al agente en Interfaz V2.
//
// A diferencia del Director de Estudio (catálogo enorme con 40+ tools), acá
// ofrecemos solo lo que ese flujo necesita: elegir producto(s), pose,
// formato y confirmar la generación. Todo es UI inline en el chat.
//
// Cada tool acá tiene su match en `executeV2Tool` dentro de `useV2Chat.ts`.
// Si agregás una, agregala en ambos lados.

export type V2Tool = {
  name: string;
  description: string;
  input_schema: {
    type: 'object';
    properties: Record<string, any>;
    required?: string[];
  };
};

export const V2_AGENT_TOOLS: V2Tool[] = [
  {
    name: 'save_memory',
    description:
      'Guarda un dato DURABLE sobre el usuario o su marca para recordarlo en futuras conversaciones (una preferencia fija, info del negocio, su tono, su público, etc.). Se ejecuta en SILENCIO (no muestra UI ni interrumpe). USALA SOLO para cosas que valga la pena recordar a largo plazo — NUNCA para detalles del pedido actual ni cosas efímeras. Si el dato actualiza/reemplaza algo que ya sabías, pasá el MISMO `key` para sobrescribir en vez de duplicar. No confirmes en voz alta que guardaste algo salvo que el usuario lo pida explícitamente.',
    input_schema: {
      type: 'object',
      properties: {
        content: {
          type: 'string',
          description:
            'El dato a recordar, en una frase clara y autocontenida. Ej: "Prefiere fondos blancos minimalistas" / "Su público son madres jóvenes de 25-35".',
        },
        key: {
          type: 'string',
          description:
            'OPCIONAL. Identificador corto y estable para datos que se actualizan con el tiempo (ej "tono_de_voz", "publico_objetivo", "estilo_visual"). Si ya existe una memoria con ese key, se sobrescribe. Omitilo para hechos sueltos que no se reemplazan.',
        },
      },
      required: ['content'],
    },
  },
  {
    name: 'activar_investigacion',
    description:
      'Habilita los SCRAPERS de datos reales (Instagram/Facebook/Google Maps/tiendas online, vía Apify), que están APAGADOS por defecto porque pueden consumir varios créditos. Al llamarla, al USUARIO le aparece un cartel de CONFIRMACIÓN (le avisa que puede tardar un momento y consumir varios créditos) con Aceptar/Cancelar. Mirá el resultado: si devuelve `{ activado: true }` el usuario aceptó → en tu PRÓXIMO turno ya podés usar los scrapers (queda habilitado para todo el chat); si devuelve `{ activado: false, cancelado: true }` el usuario canceló → NO scrapees, decíselo en una línea y ofrecé otra cosa. Llamala (sola, sin batchear) SOLO cuando vas a traer datos reales de una cuenta/marca/tienda/lugar concretos. La BÚSQUEDA WEB (web_search) NO necesita esto: está siempre disponible.',
    input_schema: {
      type: 'object',
      properties: {
        objetivo: {
          type: 'string',
          description:
            'OPCIONAL. En una línea, qué vas a investigar (ej: "Instagram de un competidor", "precios de una tienda", "tendencias del rubro"). Solo para contexto/registro.',
        },
      },
    },
  },
  {
    name: 'generate_audio',
    description:
      'Genera AUDIO a partir de texto: una locución/voz en off (TTS) o música. Mostrá el resultado reproducible en el chat. Usala cuando el usuario pide un audio, una voz, una narración, un jingle o música. Devuelve `{ confirmed: true }`.',
    input_schema: {
      type: 'object',
      properties: {
        text: {
          type: 'string',
          description:
            'Para voz: el texto EXACTO a locutar (el guion). Para música: una descripción del estilo/mood. Obligatorio.',
        },
        audio_type: {
          type: 'string',
          enum: ['voz', 'musica'],
          description: 'Tipo de audio: "voz" (locución TTS, default) o "musica".',
        },
        autogenerate: {
          type: 'boolean',
          description:
            'Si true, se genera solo apenas aparece la card (sin click). Poné true cuando ya tenés claro el texto/estilo.',
        },
      },
      required: ['text'],
    },
  },
  {
    name: 'generate_video',
    description:
      'Genera un VIDEO corto. Mostrá el resultado reproducible en el chat. IMPORTANTE: tarda VARIOS MINUTOS — avisale al usuario antes que puede demorar un par de minutos y que no cierre el chat. Devuelve `{ confirmed: true }`.\n' +
      'PODÉS controlar los FRAMES del video (muy recomendado para fidelidad):\n' +
      '• PRIMER y/o ÚLTIMO frame (`first_frame`/`last_frame`): cada uno puede salir de (a) GENERAR una imagen a propósito (source "generar" + un prompt de imagen) — ideal para escenas complejas o cuando querés que la referencia sea EXACTA (anima esa imagen, la respeta mucho más que una referencia suelta); (b) un PRODUCTO del catálogo (source "producto" + product_id); (c) un MODELO (source "modelo" + model_id); (d) una imagen ADJUNTA (source "adjunto").\n' +
      '• MODO DIÁLOGO (persona hablando): pasá `dialogue` con el guion. REQUIERE `first_frame` (la persona que habla) — generala, o usá un modelo/adjunto. Sin primer frame el diálogo no se puede.\n' +
      'Si no pasás frames, se anima directo desde el prompt. Las imágenes adjuntas igual entran como referencia.',
    input_schema: {
      type: 'object',
      properties: {
        prompt: {
          type: 'string',
          description:
            'Prompt del MOVIMIENTO / escena del video (qué pasa, cómo se mueve la cámara/el sujeto). Obligatorio.',
        },
        format: {
          type: 'string',
          description:
            'Formato de salida (slug: post / square / story / portrait / banner). Default story (9:16).',
        },
        duration: {
          type: 'number',
          description: 'Duración en segundos (default 5).',
        },
        first_frame: {
          type: 'object',
          description:
            'OPCIONAL. Imagen con la que ARRANCA el video. Obligatoria si usás `dialogue`.',
          properties: {
            source: {
              type: 'string',
              enum: ['generar', 'producto', 'modelo', 'logo', 'adjunto'],
              description:
                '"generar" (creás una imagen con `prompt`), "producto" (foto de un producto, `product_id`), "modelo" (`model_id`), "logo" (el logo de la marca), "adjunto" (imagen que adjuntó el usuario).',
            },
            prompt: {
              type: 'string',
              description: 'Si source="generar": el prompt de la imagen a generar.',
            },
            product_id: {
              type: 'string',
              description: 'Si source="producto": id del producto del catálogo.',
            },
            model_id: {
              type: 'string',
              description: 'Si source="modelo": id del modelo del catálogo.',
            },
          },
          required: ['source'],
        },
        last_frame: {
          type: 'object',
          description:
            'OPCIONAL. Imagen con la que TERMINA el video (mismo shape que first_frame). Útil para un morph/transición de A → B.',
          properties: {
            source: {
              type: 'string',
              enum: ['generar', 'producto', 'modelo', 'logo', 'adjunto'],
            },
            prompt: { type: 'string' },
            product_id: { type: 'string' },
            model_id: { type: 'string' },
          },
          required: ['source'],
        },
        dialogue: {
          type: 'object',
          description:
            'OPCIONAL. Activa el modo DIÁLOGO (una persona hablando). REQUIERE first_frame (la persona).',
          properties: {
            text: {
              type: 'string',
              description: 'El guion EXACTO que dice la persona.',
            },
            voice_id: {
              type: 'string',
              description:
                'OPCIONAL. Id de voz del catálogo para PRESELECCIONAR la voz. Si lo omitís, la card muestra un selector para que el usuario elija. Pasalo solo si el usuario indicó/se entiende qué voz va.',
            },
            motion: {
              type: 'boolean',
              description:
                'true = la persona se mueve/gesticula (dialogo_movimiento); false (default) = plano más estático.',
            },
          },
          required: ['text'],
        },
        autogenerate: {
          type: 'boolean',
          description:
            'Si true, se genera solo apenas aparece la card. Poné true cuando ya tenés claro prompt y frames.',
        },
      },
      required: ['prompt'],
    },
  },
  {
    name: 'generate_image',
    description:
      'FLUJO IMAGEN LIBRE. Genera UNA imagen a partir de un prompt libre — para CUALQUIER imagen que el usuario pida que NO sea "su producto puesto sobre un modelo" (escenas, conceptos, banners, fondos, ilustraciones, ediciones creativas, etc.). NO uses pick_product/pick_model/pick_pose en este flujo. Mostrá una card con el prompt y un botón Generar. Devuelve `{ confirmed: true }`.',
    input_schema: {
      type: 'object',
      properties: {
        prompt: {
          type: 'string',
          description:
            'Prompt detallado de la imagen a generar, en base a lo que pidió el usuario (traducí su pedido a una descripción visual rica y clara). Obligatorio.',
        },
        format: {
          type: 'string',
          description:
            'Formato de salida (slug: post / square / story / portrait / banner). Default el último formato usado por el usuario.',
        },
        autogenerate: {
          type: 'boolean',
          description:
            'Si true, la imagen se genera SOLA apenas aparece la card (sin que el usuario tenga que tocar "Generar"). Poné true cuando el prompt ya está claro y completo (el caso normal). Poné false SOLO si querés que el usuario revise/edite el prompt antes de generar.',
        },
      },
      required: ['prompt'],
    },
  },
  {
    name: 'pick_product',
    description:
      'Muestra inline en el chat la UI para que el usuario elija uno o varios productos de su catálogo, o suba uno nuevo. USÁ ESTA TOOL SIEMPRE que necesites un producto para la generación, NUNCA pidas el producto en texto. Si el usuario describió un producto en su mensaje (ej: "una remera blanca"), intentá matchear su descripción contra el catálogo y pasá ese ID en `preselected_ids` — el usuario lo va a ver marcado con check. IMPORTANTE: si la descripción matchea VARIOS productos del mismo tipo (ej: "las zapatillas" y hay varias), NO los preselecciones a todos — dejá `preselected_ids` vacío y pedile que elija cuál (sino lo obligás a destildar). Si no podés matchear con confianza, mostrá la UI igual con un mensaje pidiendo que suba la foto. Devuelve `{ product_ids: string[] }` con los IDs elegidos (puede ser uno o varios).',
    input_schema: {
      type: 'object',
      properties: {
        message: {
          type: 'string',
          description:
            'Texto breve (1 línea) que aparece arriba de la UI explicando qué necesitás. Ej: "Elegí el producto" / "Subí la foto de la remera" / "Confirmá que sea esta prenda".',
        },
        preselected_ids: {
          type: 'array',
          items: { type: 'string' },
          description:
            'IDs del catálogo que vienen pre-marcados con check. Usalo SÓLO cuando hay UN match claro y único (el usuario nombró un producto y matchea uno solo). Dejá vacío si hay varios candidatos o si el usuario tiene que elegir desde cero.',
        },
        candidate_ids: {
          type: 'array',
          items: { type: 'string' },
          description:
            'IDs candidatos que se muestran en la grilla SIN tildar, para que el usuario elija cuál. Usalo cuando el usuario describió un tipo de producto que matchea VARIOS (ej: "el vestido" y tiene varios vestidos): pasá acá esos IDs y dejá preselected_ids vacío — así ve los vestidos y toca el que quiere, sin tener que abrir "Elegir de mis productos".',
        },
        allow_multi: {
          type: 'boolean',
          description:
            'Si true, el usuario puede elegir varios productos a la vez. Default true. Poné false sólo si el agente está seguro de que tiene que ser uno solo.',
        },
      },
      required: ['message'],
    },
  },
  {
    name: 'pick_model',
    description:
      'Muestra inline la UI para que el usuario elija un modelo (la persona que aparece en la foto). Se ve como un slide horizontal con caras + cuerpo completo sobre fondo blanco. USALA DESPUÉS del producto, ANTES de la pose. Si el usuario describió específicamente al modelo en su prompt ("una mujer joven", "un hombre adulto"), pasá ese hint en `suggested_hint` para que aparezca priorizado. Si el usuario no dijo nada del modelo, llamala con `suggested_hint` vacío y el user elige. Devuelve `{ model_id: string, model_name: string }` o `{ create_new: true }` si el user pidió crear uno desde cero.',
    input_schema: {
      type: 'object',
      properties: {
        message: {
          type: 'string',
          description: 'Texto breve. Ej: "Elegí el modelo".',
        },
        suggested_hint: {
          type: 'string',
          description:
            'Hint sobre qué tipo de modelo encaja (ej: "mujer joven", "hombre adulto"). Aparece como subtítulo de la card.',
        },
      },
      required: ['message'],
    },
  },
  {
    name: 'pick_pose',
    description:
      'Muestra inline la UI para que el usuario elija UNA o VARIAS poses del catálogo del sistema (cargado desde Supabase). Si elige varias, se genera una imagen por cada pose. USALA SIEMPRE después de tener el producto. Devuelve `{ pose_ids: string[], pose_names: string[] }`.',
    input_schema: {
      type: 'object',
      properties: {
        message: {
          type: 'string',
          description:
            'Título breve (sin paréntesis ni aclaraciones). Ej: "Elegí la pose" / "¿Qué pose querés?". NO agregues "una imagen por pose" ni "podés elegir varias" — la UI ya lo muestra en la descripción.',
        },
        suggested_pose_ids: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Hasta 3 IDs de pose recomendados que aparecen primero con un badge "Recomendado". OJO: solo pasá IDs de pose que existan de verdad — NO los inventes. Como normalmente no tenés el catálogo de poses a mano, dejá esto vacío y que el usuario elija de la grilla.',
        },
      },
      required: ['message'],
    },
  },
  {
    name: 'pick_format',
    description:
      'Muestra inline la UI para elegir el formato de salida (Cuadrado, Vertical, Horizontal, Story, Banner). USALA SOLO cuando el usuario menciona explícitamente que quiere un formato distinto al actual, o cuando no hay un formato guardado de antes. En la mayoría de los casos NO la uses: el sistema reusa el último formato del usuario y vos lo recibís en el system prompt.',
    input_schema: {
      type: 'object',
      properties: {
        message: {
          type: 'string',
          description: 'Texto breve. Ej: "Elegí el formato".',
        },
        suggested_format: {
          type: 'string',
          description:
            'Formato que te parece más adecuado (aparece pre-seleccionado).',
        },
      },
      required: ['message'],
    },
  },
  {
    name: 'pick_background',
    description:
      'Muestra inline la UI para que el usuario elija el FONDO de la imagen: automático, blanco, uno de la biblioteca, uno guardado, o describirlo en palabras. USALA en el flujo con modelo SOLO cuando no estés seguro del fondo y quieras que el usuario decida. Si ya sabés el fondo (lo dijo o lo podés describir), NO la muestres: hornealo en `background` de confirm_generate. Devuelve la selección de fondo elegida.',
    input_schema: {
      type: 'object',
      properties: {
        message: {
          type: 'string',
          description: 'Texto breve. Ej: "Elegí el fondo".',
        },
        suggested: {
          type: 'object',
          description:
            'OPCIONAL. Fondo sugerido que aparece pre-seleccionado (mismo shape que `background` de confirm_generate).',
          properties: {
            mode: {
              type: 'string',
              enum: ['auto', 'blanco', 'descripcion', 'biblioteca', 'guardado'],
            },
            id: { type: 'number' },
            text: { type: 'string' },
          },
          required: ['mode'],
        },
      },
      required: ['message'],
    },
  },
  {
    name: 'confirm_generate',
    description:
      'Tool final que DISPARA la generación de la imagen con modelo. USALA SIEMPRE como último paso del flujo con modelo. Toma su configuración de dos fuentes: (1) las cards de picker resueltas que la preceden (pick_product/pick_model/pick_pose) — útil cuando mostraste pickers y batcheaste confirm_generate en el mismo turno; y (2) los valores que vos le pasás directamente acá (`product_ids`, `model_id`, `pose_ids`) — útil cuando NO mostraste un picker porque ya estabas seguro y querés hornear el valor de fondo. Lo horneado GANA sobre los pickers y no hace falta mostrarlos. IMPORTANTE: `scene_summary` es SOLO para mostrar en la card, NO se manda al motor. Lo único que llega al motor como dirección creativa extra (escena, ambiente, luz, acción, rasgos del modelo si va automático) es `instruccion`: todo lo que el usuario describió y no sea producto/modelo/pose/formato VA EN `instruccion`, sino no se refleja en la imagen. Devuelve `{ confirmed: true }`.',
    input_schema: {
      type: 'object',
      properties: {
        format: {
          type: 'string',
          description:
            'Formato a usar (ej: "Cuadrado", "Vertical"). Es el último guardado del usuario si no se cambió. Obligatorio.',
        },
        scene_summary: {
          type: 'string',
          description:
            'Resumen corto en una frase de la escena, SOLO para mostrar en la card (NO se manda al motor). Ej: "Modelo en la playa al atardecer con la remera blanca". Obligatorio.',
        },
        product_ids: {
          type: 'array',
          items: { type: 'string' },
          description:
            'OPCIONAL. IDs de producto del catálogo para HORNEAR el producto de fondo SIN mostrar pick_product — usalo cuando hay UN match claro e inequívoco con lo que pidió el usuario (o el producto vino adjunto). Si lo pasás, no llames pick_product. Si hay dudas o varios candidatos, dejá esto vacío y mostrá pick_product en su lugar.',
        },
        model_id: {
          type: 'string',
          description:
            'OPCIONAL. ID de un modelo guardado del catálogo para HORNEAR el modelo SIN mostrar pick_model — usalo solo cuando el usuario nombró un modelo concreto de la lista. Si no, omitilo: queda modelo automático (y los rasgos descriptos van en `instruccion`).',
        },
        pose_ids: {
          type: 'array',
          items: { type: 'string' },
          description:
            'OPCIONAL. IDs de pose del catálogo para HORNEAR la(s) pose(s) SIN mostrar pick_pose — usalo cuando matcheás 1+ poses claras con lo que pidió el usuario. Se genera una imagen por pose. Si no estás seguro, omitilo y mostrá pick_pose.',
        },
        background: {
          type: 'object',
          description:
            'OPCIONAL. Fondo a HORNEAR SIN mostrar pick_background. Si lo omitís → fondo automático. Lo más flexible es `descripcion` (describís el fondo en palabras). Si no estás seguro del fondo, omitilo y mostrá pick_background.',
          properties: {
            mode: {
              type: 'string',
              enum: ['auto', 'blanco', 'descripcion', 'biblioteca', 'guardado'],
              description:
                '"auto" (automático), "blanco" (fondo blanco liso), "descripcion" (lo describís en `text`), "biblioteca" (un fondo del catálogo, su `id`), "guardado" (un fondo guardado del usuario, su `id`).',
            },
            id: {
              type: 'number',
              description: 'Para biblioteca/guardado: el id del catálogo de fondos.',
            },
            text: {
              type: 'string',
              description: 'Para descripcion: el fondo descripto en palabras (escena, lugar, luz).',
            },
          },
          required: ['mode'],
        },
        instruccion: {
          type: 'string',
          description:
            'Dirección creativa extra que SÍ llega al motor (instruccion_adicional). Meté acá TODO lo que el usuario describió y no entra en producto/modelo/pose/formato: la escena, el ambiente, la luz, el encuadre, la acción, y los rasgos del modelo si va en automático (ej "modelo: mujer joven, caminando descalza en la playa al atardecer"). Aparece pre-cargada en un campo editable. Dejá vacío solo si no hay nada extra que el usuario haya pedido.',
        },
        autogenerate: {
          type: 'boolean',
          description:
            'Si true, la imagen se genera SOLA (sin click de "Generar" ni revisión del resumen). Poné true cuando horneaste todo con certeza, o cuando el usuario eligió todo desde pickers vacíos (ya decidió él). Poné false (mostrar resumen para revisar antes de gastar créditos) cuando PRESELECCIONASTE opciones por inferencia que el usuario no confirmó explícitamente — así puede corregir un guess errado antes de generar.',
        },
      },
      required: ['format', 'scene_summary'],
    },
  },
];
